<script lang="ts">
const dialogCls = cva("dialog", {
	variants: {
		fullScreen: {
			false: null,
			true: "dialog-fullscreen",
		},
		position: {
			center: null,
			top: "dialog-top",
			bottom: "dialog-bottom",
		},
	},
	defaultVariants: {
		position: "center",
		fullScreen: false,
	},
});

type variant = VariantProps<typeof dialogCls>;
type Position = variant["position"];

export interface DialogProps extends ClassProps, DeprecatedCustomSizeProps {
	title?: string;
	description?: string;
	persistent?: boolean;
	fullScreen?: variant["fullScreen"] & boolean;
	position?: variant["position"];

	closeButton?: boolean;

	/** @deprecated use position instead */
	top?: boolean;

	/** @deprecated use position instead */
	bottom?: boolean;
}
</script>

<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import { cva, type VariantProps } from "class-variance-authority";
import {
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogOverlay,
	DialogPortal,
	DialogRoot,
	DialogTitle,
	DialogTrigger,
	VisuallyHidden,
} from "reka-ui";
import { computed, ref, warn } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { deprecatedSizeToCSS, type DeprecatedCustomSizeProps } from "../../lib/size";
import { cn, tw } from "../../lib/utils";
import Icon from "../icon/Icon.vue";

const modelValue = defineModel<boolean>();

const props = defineProps<DialogProps>();

const position = computed<Position>(() => {
	if (props.position != null) {
		return props.position;
	}

	if (props.top) {
		return "top";
	}

	if (props.bottom) {
		return "bottom";
	}
});

const style = computed(() => {
	const style = deprecatedSizeToCSS(props);
	if (Object.keys(style).length > 0) {
		warn(
			"[UI] Using deprecated size props for Dialog. Please use tailwindcss utility class instead."
		);
	}

	return style;
});

defineSlots<{
	default(): void;
	trigger(): void;

	/** @deprecated use trigger instead */
	activator(): void;
}>();

const bounce = ref(false);

function handlePointerDownOutside(event: Event) {
	if (props.persistent) {
		event.preventDefault();

		bounce.value = true;
		setTimeout(() => {
			bounce.value = false;
		}, 150);
	}
}

const bounceClasses = computed(() => {
	if (!bounce.value) {
		return "";
	}

	return tw`scale-[1.02] transition-transform duration-100`;
});
</script>

<template>
	<DialogRoot v-model:open="modelValue">
		<DialogTrigger asChild>
			<slot name="trigger">
				<slot name="activator" />
			</slot>
		</DialogTrigger>
		<DialogPortal>
			<DialogOverlay class="dialog-overlay" />
			<DialogContent
				:class="cn(dialogCls({ ...props, position }), bounceClasses, props.class)"
				:style="style"
				@pointerDownOutside="handlePointerDownOutside"
			>
				<VisuallyHidden asChild>
					<DialogTitle>{{ props.title }}</DialogTitle>
				</VisuallyHidden>

				<VisuallyHidden asChild>
					<DialogDescription>{{ props.description }}</DialogDescription>
				</VisuallyHidden>

				<slot />

				<DialogClose v-if="props.closeButton" class="dialog-close">
					<Icon :d="mdiClose" />
					<span class="sr-only">Close</span>
				</DialogClose>
			</DialogContent>
		</DialogPortal>
	</DialogRoot>
</template>
